import clsx from "clsx";
import { ChevronLeftIcon } from "lucide-react";
import Link from "next/link";
import type * as React from "react";

interface DashboardLayoutHeaderProps {
	title?: string;
	description?: string;
	primaryCta?: {
		label: string;
		href?: string;
		target?: string;
		onClick?: () => void;
	};

	secondaryCta?: {
		label: string;
		href?: string;
		target?: string;
		onClick?: () => void;
	};

	backBtn?: {
		label: string;
		href: string;
	};
}

const DashboardLayoutHeader: React.FC<DashboardLayoutHeaderProps> = ({
	title,
	description,
	primaryCta,
	secondaryCta,
	backBtn,
}) => {
	return (
		<div className="mt-2 mb-4 tablet:mb-8 flex flex-col gap-2">
			{backBtn && (
				<Link
					href={backBtn.href || "#"}
					className="group -mt-1 mb-1 -ml-1 flex items-center gap-1 font-medium laptop:text-base text-sm hover:opacity-75"
				>
					<ChevronLeftIcon className="size-5 transition-transform group-hover:-translate-x-0.5" />
					{backBtn.label}
				</Link>
			)}

			<div className="flex tablet:flex-row flex-col tablet:items-center justify-between gap-4">
				<div className="flex flex-col gap-2 laptop:gap-4">
					<h1 className="flex font-bold laptop:text-2xl text-xl">{title}</h1>
					<p className="laptop:text-base text-sm">{description}</p>
				</div>

				<div className="flex gap-4">
					{secondaryCta &&
						(secondaryCta.onClick ? (
							<button
								onClick={secondaryCta.onClick}
								className={clsx(
									"flex flex-1 items-center justify-center whitespace-nowrap rounded-full border bg-white px-4 py-1.5 font-medium shadow shadow-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:shadow-slate-950 dark:hover:bg-slate-600",
								)}
							>
								{secondaryCta.label}
							</button>
						) : (
							<Link
								href={secondaryCta.href || "#"}
								target={secondaryCta.target}
								className={clsx(
									"flex flex-1 items-center justify-center whitespace-nowrap rounded-full border bg-white px-4 py-1.5 font-medium shadow shadow-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:shadow-slate-950 dark:hover:bg-slate-600",
									{
										"pointer-events-none opacity-50": !secondaryCta.href,
									},
								)}
							>
								{secondaryCta.label}
							</Link>
						))}

					{primaryCta &&
						(primaryCta.onClick ? (
							<button
								onClick={primaryCta.onClick}
								className={clsx(
									"flex flex-1 items-center justify-center whitespace-nowrap rounded-full bg-sky-500 px-4 py-1.5 font-medium text-white shadow shadow-slate-200 hover:bg-sky-600 dark:bg-sky-700 dark:shadow-slate-950",
								)}
							>
								{primaryCta.label}
							</button>
						) : (
							<Link
								target={primaryCta.target}
								href={primaryCta.href || "#"}
								className={clsx(
									"flex flex-1 items-center justify-center whitespace-nowrap rounded-full bg-sky-500 px-4 py-1.5 font-medium text-white shadow shadow-slate-200 hover:bg-sky-600 dark:bg-sky-700 dark:shadow-slate-950",
									{
										"pointer-events-none opacity-50": !primaryCta.href,
									},
								)}
							>
								{primaryCta.label}
							</Link>
						))}
				</div>
			</div>
		</div>
	);
};

export default DashboardLayoutHeader;
